import { ROUTE_ACTION_PLAN } from '@/constants/routes';
import { useSaveActionPlanMutation } from '@/queries/action-plan.queries';
import { useInterventions } from '@/queries/interventions.queries';
import { useSetFooterIsVisible } from '@/queries/layout.queries';
import { getRoute } from '@/utils';
import { useDisclosure } from '@cmpsr/components';
import { useEffect, useState } from 'react';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Intervention, InterventionStatus } from '../../types';

export default function useBuildActionPlanController() {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showError, setShowError] = useState(false);
  const [interventionsToRemove, setInterventionsToRemove] = useState<Intervention[]>([]);
  const navigate = useNavigate();
  const mutateFooterIsVisible = useSetFooterIsVisible();
  const { data, isLoading } = useInterventions();
  const saveActionPlanMutation = useSaveActionPlanMutation();
  const interventions = data?.interventions.filter(({ status }) => status !== InterventionStatus.COMPLETED) || [];

  const {
    isOpen: isRemoveActiveInterventionsOpen,
    onClose: onCloseRemoveActiveInterventions,
    onOpen: onOpenRemoveActiveInterventions,
  } = useDisclosure();
  const activeInterventions = interventions.filter(({ status }) => status === InterventionStatus.IN_PROGRESS);

  const isMaxSelectionReached = (id: string) => selectedItems.length >= 5 && !selectedItems.includes(id);

  const handleItemSelectionChange = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter((selectedId) => selectedId !== id));
    }
  };

  const handleRemoveActiveInterventions = async () => {
    try {
      await saveActionPlanMutation.mutateAsync({ ids: selectedItems });
      navigate(getRoute(ROUTE_ACTION_PLAN));
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveActionPlan = async () => {
    if (!selectedItems.length) {
      setShowError(true);
      return;
    }
    setShowError(false);

    if (selectedItems.length < activeInterventions.length) {
      const itemsToRemove = activeInterventions.filter(({ id }) => !selectedItems.includes(id));
      setInterventionsToRemove(itemsToRemove);
      onOpenRemoveActiveInterventions();
      return;
    } else {
      try {
        await saveActionPlanMutation.mutateAsync({ ids: selectedItems });
        navigate(getRoute(ROUTE_ACTION_PLAN));
      } catch (error) {
        console.error(error);
      }
    }
  };

  useDeepCompareEffect(() => {
    setSelectedItems(activeInterventions.map(({ id }) => id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interventions]);

  useBeforeUnload((event) => {
    if (selectedItems.length) {
      event.preventDefault();
      event.returnValue = '';
    }
  });

  useEffect(() => {
    mutateFooterIsVisible.mutateAsync(false);
    return () => {
      mutateFooterIsVisible.mutateAsync(true);
    };
  }, []);

  return {
    handleItemSelectionChange,
    handleRemoveActiveInterventions,
    handleSaveActionPlan,
    interventions,
    interventionsToRemove,
    isLoading,
    isMaxSelectionReached,
    isRemoveActiveInterventionsOpen,
    onCloseRemoveActiveInterventions,
    selectedItems,
    showError,
  };
}
